import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Tabs, Card, Button, Input, Table, Tag, Space, Switch, Select,
  Typography, Avatar, Spin, Modal, Form, InputNumber, Popconfirm,
  message, Empty, Tooltip, Badge
} from 'antd';
import {
  RobotOutlined, SendOutlined, UserOutlined, PlusOutlined,
  EditOutlined, DeleteOutlined, SaveOutlined, ReloadOutlined,
  BookOutlined, QuestionCircleOutlined, BulbOutlined,
  ThunderboltOutlined, DatabaseOutlined, ExperimentOutlined,
  CheckCircleOutlined, WarningOutlined
} from '@ant-design/icons';
import { API_BASE, authHeaders } from '../../config/api';
import './AIPipeline.css';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const ENTRY_TYPES = [
  { value: 'knowledge', label: 'Knowledge Base', color: '#6366f1', icon: <BookOutlined /> },
  { value: 'qa', label: 'Q&A Pair', color: '#10b981', icon: <QuestionCircleOutlined /> },
  { value: 'instruction', label: 'Instruction', color: '#f59e0b', icon: <BulbOutlined /> },
];

const CATEGORIES = [
  'general', 'analytics', 'heatmaps', 'session-recordings', 'forms',
  'pricing', 'setup', 'integrations', 'troubleshooting', 'features',
  'conversion', 'ai-copilot', 'account', 'billing', 'api'
];

const AIPipeline = () => {
  const [activeTab, setActiveTab] = useState('training');
  const [trainingData, setTrainingData] = useState([]);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filterType, setFilterType] = useState(null);
  const [filterCategory, setFilterCategory] = useState(null);
  const [seeding, setSeeding] = useState(false);

  // Test chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatWelcomeLoaded, setChatWelcomeLoaded] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [testLogs, setTestLogs] = useState([]);
  const messagesEndRef = useRef(null);

  const [form] = Form.useForm();

  const getHeaders = () => ({ 'Content-Type': 'application/json', ...authHeaders() });

  const handleVerifyPipeline = async () => {
    setSeeding(true);
    try {
      const res = await fetch(`${API_BASE}/admin/ai/verify-pipeline`, {
        method: 'POST',
        headers: getHeaders()
      });
      const data = await res.json();
      if (res.ok) {
        if (data.groq_status === 'connected') {
          message.success(`AI Pipeline verified! ${data.training_entries} training entries loaded. Groq LLM connected.`);
        } else {
          message.warning(`${data.training_entries} training entries in DB, but Groq API issue: ${data.groq_status}`);
        }
        fetchTrainingData();
        fetchStats();
      } else {
        message.error(data.error || 'Failed to verify pipeline');
      }
    } catch (err) {
      message.error('Failed to verify AI pipeline');
    } finally {
      setSeeding(false);
    }
  };

  // Fetch training data
  const fetchTrainingData = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_BASE}/admin/ai/training-data?`;
      if (filterType) url += `entry_type=${filterType}&`;
      if (filterCategory) url += `category=${filterCategory}&`;
      const res = await fetch(url, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setTrainingData(data.items || []);
      }
    } catch (err) {
      message.error('Failed to load training data');
    } finally {
      setLoading(false);
    }
  }, [filterType, filterCategory]);

  // Fetch system config
  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/ai/config`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSystemPrompt(data.system_prompt || '');
      }
    } catch (err) {
      console.error('Failed to load AI config:', err);
    }
  }, []);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/ai/stats`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, []);

  // Fetch test logs
  const fetchTestLogs = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/ai/test-logs`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setTestLogs(data.items || []);
      }
    } catch (err) {
      console.error('Failed to load test logs:', err);
    }
  }, []);

  useEffect(() => {
    fetchTrainingData();
    fetchConfig();
    fetchStats();
  }, [fetchTrainingData, fetchConfig, fetchStats]);

  useEffect(() => {
    if (activeTab === 'testing') fetchTestLogs();
  }, [activeTab, fetchTestLogs]);

  // Scroll chat to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Fetch test chat welcome from backend
  useEffect(() => {
    if (activeTab !== 'testing' || chatWelcomeLoaded) return;
    setChatWelcomeLoaded(true);
    fetch(`${API_BASE}/admin/ai/test-chat`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ message: 'Introduce yourself briefly. You are in test mode for the AsumaHQ admin pipeline.' })
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data?.reply) {
          setChatMessages([{
            id: 1, type: 'ai', content: data.reply,
            confidence: data.confidence, matchedEntries: data.matched_entries || [],
            timestamp: new Date()
          }]);
        }
      })
      .catch(() => {});
  }, [activeTab, chatWelcomeLoaded]);

  // Save system prompt
  const saveSystemPrompt = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/admin/ai/config`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ system_prompt: systemPrompt })
      });
      if (res.ok) {
        message.success('System prompt saved');
      } else {
        message.error('Failed to save system prompt');
      }
    } catch (err) {
      message.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Add/Edit training entry
  const handleSaveEntry = async (values) => {
    setSaving(true);
    try {
      const url = editingEntry
        ? `${API_BASE}/admin/ai/training-data/${editingEntry.id}`
        : `${API_BASE}/admin/ai/training-data`;
      const method = editingEntry ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(values)
      });
      if (res.ok) {
        message.success(editingEntry ? 'Entry updated' : 'Entry created');
        setShowAddModal(false);
        setEditingEntry(null);
        form.resetFields();
        fetchTrainingData();
        fetchStats();
      } else {
        const data = await res.json().catch(() => ({}));
        message.error(data.error || 'Failed to save entry');
      }
    } catch (err) {
      message.error('Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  // Delete training entry
  const handleDeleteEntry = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/admin/ai/training-data/${id}`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      if (res.ok) {
        message.success('Entry deleted');
        fetchTrainingData();
        fetchStats();
      }
    } catch (err) {
      message.error('Failed to delete entry');
    }
  };

  // Toggle entry active status
  const handleToggleActive = async (entry) => {
    try {
      await fetch(`${API_BASE}/admin/ai/training-data/${entry.id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ ...entry, is_active: !entry.is_active })
      });
      fetchTrainingData();
      fetchStats();
    } catch (err) {
      message.error('Failed to update');
    }
  };

  // Open edit modal
  const openEditModal = (entry) => {
    setEditingEntry(entry);
    form.setFieldsValue({
      entry_type: entry.entry_type,
      category: entry.category,
      title: entry.title,
      content: entry.content,
      match_patterns: entry.match_patterns,
      response_template: entry.response_template,
      priority: entry.priority,
      is_active: entry.is_active
    });
    setShowAddModal(true);
  };

  // Open add modal
  const openAddModal = () => {
    setEditingEntry(null);
    form.resetFields();
    form.setFieldsValue({ entry_type: 'knowledge', category: 'general', priority: 0, is_active: true });
    setShowAddModal(true);
  };

  // Test chat send
  const handleTestSend = async () => {
    if (!chatInput.trim() || chatLoading) return;
    const userMsg = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, {
      id: Date.now(),
      type: 'user',
      content: userMsg,
      timestamp: new Date()
    }]);
    setChatLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/ai/test-chat`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ message: userMsg })
      });
      const data = await res.json();
      if (res.ok) {
        setChatMessages(prev => [...prev, {
          id: Date.now() + 1,
          type: 'ai',
          content: data.reply,
          confidence: data.confidence,
          matchedEntries: data.matched_entries || [],
          timestamp: new Date()
        }]);
      } else {
        setChatMessages(prev => [...prev, {
          id: Date.now() + 1,
          type: 'ai',
          content: `Error: ${data.error || 'Failed to get response'}`,
          confidence: 0,
          matchedEntries: [],
          timestamp: new Date()
        }]);
      }
    } catch (err) {
      setChatMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'ai',
        content: 'Connection error. Please check the backend is running.',
        confidence: 0,
        matchedEntries: [],
        timestamp: new Date()
      }]);
    } finally {
      setChatLoading(false);
      fetchTestLogs();
    }
  };

  const handleChatKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleTestSend();
    }
  };

  const formatMsgContent = (content) => {
    const parts = content.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  const getConfidenceClass = (confidence) => {
    if (confidence >= 60) return 'confidence-high';
    if (confidence >= 30) return 'confidence-medium';
    return 'confidence-low';
  };

  // Training data table columns
  const columns = [
    {
      title: 'Type',
      dataIndex: 'entry_type',
      key: 'entry_type',
      width: 130,
      render: (type) => {
        const config = ENTRY_TYPES.find(t => t.value === type) || {};
        return <Tag className="entry-type-tag" color={config.color}>{config.icon} {config.label || type}</Tag>;
      }
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 130,
      render: (cat) => <Tag style={{ borderRadius: 6 }}>{cat}</Tag>
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (title) => <Text strong>{title || '—'}</Text>
    },
    {
      title: 'Content',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
      width: 250,
      render: (content) => <Text type="secondary" style={{ fontSize: 13 }}>{content?.substring(0, 100)}{content?.length > 100 ? '...' : ''}</Text>
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      align: 'center',
      sorter: (a, b) => a.priority - b.priority,
      render: (p) => <Badge count={p} showZero style={{ backgroundColor: p > 0 ? '#6366f1' : '#d1d5db' }} />
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      align: 'center',
      render: (active, record) => (
        <Switch
          size="small"
          checked={active}
          onChange={() => handleToggleActive(record)}
        />
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Edit">
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
          </Tooltip>
          <Popconfirm title="Delete this entry?" onConfirm={() => handleDeleteEntry(record.id)} okText="Delete" cancelText="Cancel">
            <Tooltip title="Delete">
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className="ai-pipeline-container">
      {/* Header */}
      <div className="ai-pipeline-header">
        <div className="ai-pipeline-header-left">
          <h2><RobotOutlined style={{ marginRight: 10 }} />AsumaHQ AI Pipeline</h2>
          <p>Train, configure, and test your AI assistant</p>
        </div>
        <div className="ai-pipeline-header-actions">
          <Button
            icon={<ThunderboltOutlined />}
            loading={seeding}
            onClick={handleVerifyPipeline}
            style={{ borderRadius: 8, fontWeight: 600, background: 'rgba(255,255,255,0.2)', borderColor: 'rgba(255,255,255,0.6)', color: '#fff' }}
          >
            Verify AI Pipeline
          </Button>
        </div>
        <div className="ai-pipeline-stats">
          <div className="ai-pipeline-stat">
            <div className="ai-pipeline-stat-value">{stats?.total_entries ?? '—'}</div>
            <div className="ai-pipeline-stat-label">Entries</div>
          </div>
          <div className="ai-pipeline-stat">
            <div className="ai-pipeline-stat-value">{stats?.total_tests ?? '—'}</div>
            <div className="ai-pipeline-stat-label">Tests Run</div>
          </div>
          <div className="ai-pipeline-stat">
            <div className="ai-pipeline-stat-value">{stats?.avg_confidence ?? '—'}%</div>
            <div className="ai-pipeline-stat-label">Avg Confidence</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs activeKey={activeTab} onChange={setActiveTab} className="ai-pipeline-tabs" size="large" items={[
        {
          key: 'training',
          label: <span><DatabaseOutlined style={{ marginRight: 6 }} />Training Data</span>,
          children: (
            <>
              {/* System Prompt */}
              <Card
                className="system-prompt-card"
                title={<span><ThunderboltOutlined style={{ marginRight: 8, color: '#6366f1' }} />System Prompt</span>}
                extra={
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={saving}
                    onClick={saveSystemPrompt}
                    style={{ background: '#6366f1', borderColor: '#6366f1', borderRadius: 8 }}
                  >
                    Save
                  </Button>
                }
              >
                <TextArea
                  className="system-prompt-textarea"
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  rows={4}
                  placeholder="Define AsumaHQ AI's personality, role, and behaviour..."
                />
                <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                  This system prompt defines AsumaHQ AI's identity and guides how it responds to users.
                </Text>
              </Card>

              {/* Filters + Add Button */}
              <Card
                className="training-table-card"
                title={<span><BookOutlined style={{ marginRight: 8, color: '#6366f1' }} />Training Entries</span>}
                extra={
                  <Space>
                    <Select
                      allowClear
                      placeholder="Filter by type"
                      style={{ width: 160 }}
                      value={filterType}
                      onChange={setFilterType}
                    >
                      {ENTRY_TYPES.map(t => (
                        <Option key={t.value} value={t.value}>{t.icon} {t.label}</Option>
                      ))}
                    </Select>
                    <Select
                      allowClear
                      placeholder="Filter by category"
                      style={{ width: 160 }}
                      value={filterCategory}
                      onChange={setFilterCategory}
                    >
                      {CATEGORIES.map(c => (
                        <Option key={c} value={c}>{c}</Option>
                      ))}
                    </Select>
                    <Button icon={<ReloadOutlined />} onClick={() => { fetchTrainingData(); fetchStats(); }} />
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={openAddModal}
                      style={{ background: '#6366f1', borderColor: '#6366f1', borderRadius: 8 }}
                    >
                      Add Entry
                    </Button>
                  </Space>
                }
              >
                <Table
                  dataSource={trainingData}
                  columns={columns}
                  rowKey="id"
                  loading={loading}
                  pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `${total} entries` }}
                  locale={{ emptyText: <Empty description="No training data yet. Click 'Add Entry' to get started." /> }}
                  size="middle"
                />
              </Card>
            </>
          )
        },
        {
          key: 'testing',
          label: <span><ExperimentOutlined style={{ marginRight: 6 }} />Test AI</span>,
          children: (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20 }}>
              {/* Chat Panel */}
              <div>
                <Card className="test-chat-card" bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <div className="test-chat-messages">
                    {chatMessages.map((msg) => (
                      <div key={msg.id} className={`test-msg ${msg.type === 'user' ? 'test-msg-user' : 'test-msg-ai'}`}>
                        <Avatar
                          size={36}
                          icon={msg.type === 'user' ? <UserOutlined /> : <RobotOutlined />}
                          style={{ backgroundColor: msg.type === 'user' ? '#6366f1' : '#10b981', flexShrink: 0 }}
                        />
                        <div>
                          <div className="test-msg-bubble">
                            <div className="test-msg-text">{formatMsgContent(msg.content)}</div>
                            <div className="test-msg-meta">
                              {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              {msg.type === 'ai' && msg.confidence !== null && msg.confidence !== undefined && (
                                <span className={`confidence-badge ${getConfidenceClass(msg.confidence)}`}>
                                  {msg.confidence >= 60 ? <CheckCircleOutlined /> : <WarningOutlined />}
                                  {msg.confidence.toFixed(0)}% match
                                </span>
                              )}
                            </div>
                          </div>
                          {msg.type === 'ai' && msg.matchedEntries && msg.matchedEntries.length > 0 && (
                            <div className="matched-entries-list">
                              <Text type="secondary" style={{ fontSize: 11, fontWeight: 600 }}>Matched entries:</Text>
                              {msg.matchedEntries.map((entry, idx) => (
                                <div key={idx} className="matched-entry">
                                  <Tag color={entry.type === 'qa' ? '#10b981' : entry.type === 'instruction' ? '#f59e0b' : '#6366f1'} style={{ fontSize: 10, lineHeight: '16px', borderRadius: 4, margin: 0 }}>{entry.type}</Tag>
                                  <span>{entry.title || `#${entry.id}`}</span>
                                  <span style={{ opacity: 0.6 }}>({entry.score?.toFixed(1)} pts)</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {chatLoading && (
                      <div className="test-msg test-msg-ai">
                        <Avatar size={36} icon={<RobotOutlined />} style={{ backgroundColor: '#10b981', flexShrink: 0 }} />
                        <div className="test-msg-bubble">
                          <Space><Spin size="small" /><Text type="secondary">AsumaHQ is thinking...</Text></Space>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  <div className="test-chat-input">
                    <div className="test-chat-input-row">
                      <TextArea
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyPress={handleChatKeyPress}
                        placeholder="Test a question... (e.g. 'What are heatmaps?' or 'How do I set up tracking?')"
                        autoSize={{ minRows: 1, maxRows: 3 }}
                        style={{ borderRadius: 12 }}
                      />
                      <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={handleTestSend}
                        disabled={!chatInput.trim() || chatLoading}
                        className="test-send-btn"
                      >
                        Send
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Sidebar: Test Logs */}
              <div>
                <Card
                  className="test-logs-card"
                  title={<span style={{ fontSize: 14 }}>Recent Test Logs</span>}
                  extra={<Button size="small" icon={<ReloadOutlined />} onClick={fetchTestLogs} />}
                  style={{ height: '60vh', overflow: 'hidden' }}
                  bodyStyle={{ overflowY: 'auto', maxHeight: 'calc(60vh - 58px)', padding: '8px 12px' }}
                >
                  {testLogs.length === 0 ? (
                    <Empty description="No test logs yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  ) : (
                    testLogs.slice(0, 20).map((log) => (
                      <div key={log.id} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                          <Text strong style={{ fontSize: 12 }}>{log.user_message?.substring(0, 40)}{log.user_message?.length > 40 ? '...' : ''}</Text>
                          <span className={`confidence-badge ${getConfidenceClass(parseFloat(log.confidence || 0))}`} style={{ fontSize: 10 }}>
                            {parseFloat(log.confidence || 0).toFixed(0)}%
                          </span>
                        </div>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {new Date(log.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </Text>
                      </div>
                    ))
                  )}
                </Card>
              </div>
            </div>
          )
        }
      ]} />

      {/* Add/Edit Modal */}
      <Modal
        title={editingEntry ? 'Edit Training Entry' : 'Add Training Entry'}
        open={showAddModal}
        onCancel={() => { setShowAddModal(false); setEditingEntry(null); form.resetFields(); }}
        footer={null}
        width={640}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveEntry}
          initialValues={{ entry_type: 'knowledge', category: 'general', priority: 0, is_active: true }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="entry_type" label="Type" rules={[{ required: true }]}>
              <Select>
                {ENTRY_TYPES.map(t => (
                  <Option key={t.value} value={t.value}>{t.icon} {t.label}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="category" label="Category" rules={[{ required: true }]}>
              <Select showSearch>
                {CATEGORIES.map(c => (
                  <Option key={c} value={c}>{c}</Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <Form.Item name="title" label="Title">
            <Input placeholder="Brief title for this entry" />
          </Form.Item>

          <Form.Item name="content" label="Content / Knowledge" rules={[{ required: true, message: 'Content is required' }]}>
            <TextArea rows={4} placeholder="The main knowledge content or question text..." />
          </Form.Item>

          <Form.Item
            name="match_patterns"
            label={<span>Match Patterns <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>(comma-separated keywords)</Text></span>}
          >
            <Input placeholder="e.g. heatmap, click map, scroll map, heat map" />
          </Form.Item>

          <Form.Item
            name="response_template"
            label={<span>Response Template <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>(for Q&A type — the answer)</Text></span>}
          >
            <TextArea rows={3} placeholder="The response AsumaHQ AI should give when this entry matches..." />
          </Form.Item>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="priority" label="Priority (higher = more important)">
              <InputNumber min={0} max={100} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="is_active" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={() => { setShowAddModal(false); setEditingEntry(null); }}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={saving}
              icon={<SaveOutlined />}
              style={{ background: '#6366f1', borderColor: '#6366f1' }}
            >
              {editingEntry ? 'Update' : 'Create'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AIPipeline;
